import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import {
  User,
  UserIndex,
  UserStatus,
  UserStatusPrimaryDisplay,
  UserStatusSecondaryLabel,
} from 'src/app/_models/user';
import { UserService } from 'src/app/_services/user.service';
import { ModalService } from 'src/app/_services/modal.service';
import { DropdownFormFieldComponent } from 'src/app/_components/forms/dropdown-form-field/dropdown-form-field.component';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { IconButtonComponent } from 'src/app/_components/buttons/icon-button/icon-button.component';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';
import { MatIconButtonComponent } from 'src/app/_components/buttons/mat-icon-button/mat-icon-button.component';
import { FilterModalComponent } from 'src/app/_components/modals/filter-modal/filter-modal.component';
import { ModalComponent } from 'src/app/_components/modals/modal.component';
import { SearchFieldComponent } from 'src/app/_components/forms/search-field/search-field.component';
import { StatusIndicatorComponent } from 'src/app/_components/chips/status-indicator/status-indicator.component';
import { ToastComponent } from 'src/app/_components/toast/toast.component';
import { ValidationModalComponent } from 'src/app/_components/modals/validation-modal/validation-modal.component';
import { FormatRolePipe } from 'src/app/_helpers/format-role.pipe';
import { Role } from 'src/app/_models/role';
import { MatIcon } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Laboratory } from 'src/app/_models/laboratory';
import { AuthenticationService } from 'src/app/_services/authentication.service';

@Component({
  selector: 'app-administration-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownFormFieldComponent,
    ErrorPageComponent,
    FilterModalComponent,
    FormatRolePipe,
    IconButtonComponent,
    LoadingSpinnerComponent,
    MatIcon,
    MatIconButtonComponent,
    MatTableModule,
    MatTooltip,
    ModalComponent,
    SearchFieldComponent,
    StatusIndicatorComponent,
    ToastComponent,
    ValidationModalComponent,
  ],
})
export class AdministrationUsersComponent implements OnInit, OnDestroy {
  readonly displayedColumns = [
    'firstName',
    'lastName',
    'email',
    'organisation',
    'role',
    'laboratories',
    'status',
    'actions',
  ];

  private readonly userService = inject(UserService);
  private readonly modalService = inject(ModalService);
  protected authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  private pageRequested = 1;
  private pageSize = 5;
  private successToast?: ReturnType<typeof setTimeout>;

  readonly UserStatus = UserStatus;
  readonly UserStatusPrimaryDisplay = UserStatusPrimaryDisplay;
  readonly UserStatusSecondaryLabel = UserStatusSecondaryLabel;

  laboratory: Laboratory;
  users = signal<User[]>([]);
  isFirstLoad = signal(true);
  loadError = signal<ErrorObject>(null);
  actionError = signal<ErrorObject>(null);
  currentSort = signal({ sortBy: 'FirstName', sortDirection: 'asc' });
  currentPage = signal(1);
  totalPages = signal(0);
  hasNext = signal(false);
  hasPrevious = signal(false);

  selectedUserIdForDeletion = signal<number | null>(null);
  toastMessage = signal<string | null>(null);
  toastStyle = signal<'positive' | 'critical'>('positive');
  toastIcon = signal<string>('checkmark');
  displayToast = signal(false);

  hasActiveFilters = signal(false);

  deleteUserProcessing = signal(false);
  deleteInviteProcessing = signal(false);
  resendInviteProcessing = signal(false);
  resendConfirmationEmailProcessing = signal(false);
  resendUpdateEmailConfirmationProcessing = signal(false);

  searchField = new FormBase<string>({
    key: 'search',
    label: 'Search',
    type: 'text',
    placeholder: 'Search...',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  roleFilterField = new FormBase({
    key: 'role',
    label: 'Role',
    type: 'dropdown',
    placeholder: 'Select role...',
    disabled: false,
    required: false,
    value: null,
    options: [
      { key: null, value: 'All' },
      { key: Role.OGI_Super_Administrator, value: 'OGI Super Administrator' },
      { key: Role.OGI_Laboratory_Administrator, value: 'OGI Laboratory Administrator' },
      { key: Role.Super_User, value: 'Super User' },
      { key: Role.Lab_Administrator, value: 'Lab Administrator' },
      { key: Role.Edit_Access_User, value: 'Edit Access User' },
      { key: Role.Run_Access_User, value: 'Run Access User' },
      { key: Role.Read_Access_User, value: 'Read Access User' },
    ],
  });

  usersPerPageField = new FormBase({
    key: 'usersPerPage',
    label: 'Users Per Page',
    type: 'dropdown',
    placeholder: '',
    disabled: false,
    required: false,
    value: '5',
    options: [
      { key: '5', value: '5' },
      { key: '10', value: '10' },
      { key: '25', value: '25' },
      { key: '50', value: '50' },
      { key: '100', value: '100' },
    ],
  });

  tableControls = this.formBuilder.group({
    [this.searchField.key]: new FormControl(this.searchField.value),
    [this.usersPerPageField.key]: new FormControl(this.usersPerPageField.value),
    [this.roleFilterField.key]: new FormControl(this.roleFilterField.value),
  });

  ngOnInit(): void {
    this.tableControls
      .get(this.usersPerPageField.key)
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.pageSize = value as number;
        this.pageRequested = 1;
        this.getUsers();
      });

    this.laboratory = this.authenticationService.selectedLaboratory;
    this.getUsers();
  }

  getUsers(): void {
    const search = this.tableControls.controls['search'].value as string;
    const role = this.tableControls.controls['role'].value as string;
    const sort = this.currentSort();

    this.userService
      .getAllUsers(
        sort.sortBy,
        sort.sortDirection,
        this.pageRequested,
        this.pageSize,
        typeof search === 'string' && search ? search : undefined,
        role ?? undefined,
      )
      .subscribe({
        next: (result: UserIndex) => {
          this.loadError.set(null);
          this.users.set(result.users);
          this.currentPage.set(result.paginationData.CurrentPage);
          this.totalPages.set(result.paginationData.TotalPages);
          this.hasNext.set(result.paginationData.HasNext);
          this.hasPrevious.set(result.paginationData.HasPrevious);
          this.isFirstLoad.set(false);
        },
        error: (err: ErrorObject) => {
          if (this.isFirstLoad()) {
            // Initial load failed: there is no table to fall back to, so show
            // the full-page error state.
            this.loadError.set(err);
            this.isFirstLoad.set(false);
          } else {
            // A pagination/sort/search/filter request failed: keep the
            // already-rendered table and surface the failure via a toast.
            this.showToast('Failed to load users', 'critical', 'warning');
          }
        },
      });
  }

  onSort(sortTerm: string): void {
    const current = this.currentSort();
    if (current.sortBy === sortTerm) {
      this.currentSort.set({
        ...current,
        sortDirection: current.sortDirection === 'asc' ? 'desc' : 'asc',
      });
    } else {
      this.currentSort.set({ sortBy: sortTerm, sortDirection: 'asc' });
    }
    this.pageRequested = 1;
    this.getUsers();
  }

  handleSearch(action: string): void {
    if (action === 'clear-search') {
      this.tableControls.patchValue({ search: null });
    }
    this.pageRequested = 1;
    this.getUsers();
  }

  getPrevious(): void {
    this.pageRequested = this.currentPage() - 1;
    this.getUsers();
  }

  getNext(): void {
    this.pageRequested = this.currentPage() + 1;
    this.getUsers();
  }

  getLabNames(user: User): string {
    return user.laboratories?.map((lab) => lab.laboratoryName).join(', ') || '-';
  }

  onEditUser(userId: number): void {
    void this.router.navigate([`user/update-user/${userId}`]);
  }

  resendInvite(userId: number): void {
    if (this.resendInviteProcessing()) return;
    this.resendInviteProcessing.set(true);
    this.actionError.set(null);

    this.userService.resendUserInvite(userId).subscribe({
      next: () => {
        this.resendInviteProcessing.set(false);
        this.showToast('User invite resent');
        this.getUsers();
      },
      error: (err: ErrorObject) => {
        this.actionError.set(err);
        this.resendInviteProcessing.set(false);
        this.openModal('error');
      },
    });
  }

  handleInviteDeletion(userId: number): void {
    this.actionError.set(null);
    this.selectedUserIdForDeletion.set(userId);
    this.openModal('delete-invite');
  }

  deleteInvite(): void {
    if (this.deleteInviteProcessing()) return;
    this.deleteInviteProcessing.set(true);
    this.actionError.set(null);

    this.userService.deleteUserInvite(this.selectedUserIdForDeletion()).subscribe({
      next: () => {
        this.deleteInviteProcessing.set(false);
        this.selectedUserIdForDeletion.set(null);
        this.closeModal('delete-invite');
        this.showToast('User invite deleted');
        this.getUsers();
      },
      error: (err: ErrorObject) => {
        this.closeModal('delete-invite');
        this.actionError.set(err);
        this.deleteInviteProcessing.set(false);
        this.openModal('error');
      },
    });
  }

  resendConfirmationEmail(userId: number): void {
    if (this.resendConfirmationEmailProcessing()) return;
    this.resendConfirmationEmailProcessing.set(true);
    this.actionError.set(null);

    this.userService.resendConfirmation(userId).subscribe({
      next: () => {
        this.resendConfirmationEmailProcessing.set(false);
        this.showToast('Confirmation email resent');
        this.getUsers();
      },
      error: (err: ErrorObject) => {
        this.actionError.set(err);
        this.resendConfirmationEmailProcessing.set(false);
        this.openModal('error');
      },
    });
  }

  resendUpdateEmailConfirmation(userId: number): void {
    if (this.resendUpdateEmailConfirmationProcessing()) return;
    this.resendUpdateEmailConfirmationProcessing.set(true);
    this.actionError.set(null);

    this.userService.resendUpdateEmailConfirmation(userId).subscribe({
      next: () => {
        this.resendUpdateEmailConfirmationProcessing.set(false);
        this.showToast('Update email confirmation resent');
        this.getUsers();
      },
      error: (err: ErrorObject) => {
        this.actionError.set(err);
        this.resendUpdateEmailConfirmationProcessing.set(false);
        this.openModal('error');
      },
    });
  }

  handleUserDeletion(userId: number): void {
    this.actionError.set(null);
    this.selectedUserIdForDeletion.set(userId);
    this.openModal('delete-user');
  }

  deleteUser(): void {
    if (this.deleteUserProcessing()) return;
    this.deleteUserProcessing.set(true);
    this.actionError.set(null);

    this.userService.deleteUser(this.selectedUserIdForDeletion()).subscribe({
      next: () => {
        this.deleteUserProcessing.set(false);
        this.selectedUserIdForDeletion.set(null);
        this.closeModal('delete-user');
        this.showToast('User deleted successfully');
        this.getUsers();
      },
      error: (err: ErrorObject) => {
        this.closeModal('delete-user');
        this.actionError.set(err);
        this.deleteUserProcessing.set(false);
        this.openModal('error');
      },
    });
  }

  filterModalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'continue-button':
        this.hasActiveFilters.set(!!this.tableControls.controls['role'].value);
        this.pageRequested = 1;
        this.getUsers();
        this.closeModal('filter');
        break;
      case 'reset-button':
        this.clearFilters();
        break;
    }
  }

  clearFilters(): void {
    this.tableControls.patchValue({ [this.roleFilterField.key]: null });
    this.hasActiveFilters.set(false);
    this.pageRequested = 1;
    this.getUsers();
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'close-delete-invite':
        this.selectedUserIdForDeletion.set(null);
        this.closeModal('delete-invite');
        break;
      case 'delete-invite':
        this.deleteInvite();
        break;
      case 'close-delete-user':
        this.selectedUserIdForDeletion.set(null);
        this.closeModal('delete-user');
        break;
      case 'delete-user':
        this.deleteUser();
        break;
    }
  }

  showToast(
    message: string,
    toastStyle: 'positive' | 'critical' = 'positive',
    icon = 'checkmark',
  ): void {
    this.toastMessage.set(message);
    this.toastStyle.set(toastStyle);
    this.toastIcon.set(icon);
    this.displayToast.set(true);
    if (this.successToast) {
      clearTimeout(this.successToast);
    }
    this.successToast = setTimeout(() => {
      this.displayToast.set(false);
    }, 5000);
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.successToast) {
      clearTimeout(this.successToast);
    }
  }
}
